import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Calendar, MoreVertical, Pencil, Trash2, Sparkles, FileText, Archive, Search } from 'lucide-react';
import { apiGet, apiPost, apiDelete, apiPatch } from '../lib/api';

interface MeetingTemplate {
  id: string;
  name: string;
  description: string;
  content: object;
}
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { getLocalToday } from '../lib/dates';
import { TimeSelect } from '../components/TimeSelect';
import { excerptDeleteWarning } from '../lib/excerpts';
import { SeriesMeetingList } from '../components/series/SeriesTabs';
import { buildMeetingItems, hasWrittenNotes } from '../components/series/PastMeetings';

// Long lists show this many, the last fading into a Show more button (#319).
const SHOWN_BEFORE_MORE = 6;
const FADE_MASK = 'linear-gradient(to bottom, #000 calc(100% - 3.5rem), transparent)';
const fadeStyle = (faded: boolean): React.CSSProperties | undefined =>
  (faded ? { maskImage: FADE_MASK, WebkitMaskImage: FADE_MASK } : undefined);

function MoreToggle({ open, label, onToggle }: { open: boolean; label: string; onToggle: () => void }) {
  return (
    <div className="mt-1 flex justify-center">
      <button type="button" onClick={onToggle} aria-expanded={open}
        className="rounded-md px-3 py-1 text-xs font-medium text-accent hover:bg-accent/10">
        {open ? 'Show less' : label}
      </button>
    </div>
  );
}

interface MeetingSeries {
  id: string;
  name: string;
  description: string | null;
  cadence: string | null;
  end_date: string | null;
  instance_count: number;
  created_at: string;
}

interface StandaloneMeeting {
  id: string;
  title: string | null;
  meeting_date: string;
  meeting_time: string | null;
  duration_minutes: number | null;
  summary: string | null;
  created_at: string;
  note_content?: string | null;
}

/** What came out of the one-off meetings (#308), from GET /meeting-instances/standalone/items. */
interface StandaloneItems {
  tasks: { id: string; title: string; status: string; meeting_id: string; follow_up: boolean }[];
  notes: { id: string; title: string; meeting_id: string }[];
  attendees: { meeting_id: string; name: string }[];
}
const NO_ITEMS: StandaloneItems = { tasks: [], notes: [], attendees: [] };

/** Series whose name or description contain every word of `query`, ignoring case (#319). */
export function filterSeries<S extends { name: string; description?: string | null }>(list: S[], query: string): S[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return list;
  return list.filter((s) => {
    const hay = `${s.name}\n${s.description ?? ''}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

/** Soonest first: by date, then time, with untimed ones last on their day (#319). */
export function bySoonest<T extends { date: string; time: string | null }>(a: T, b: T): number {
  return a.date.localeCompare(b.date) || Number(a.time === null) - Number(b.time === null) || (a.time ?? '').localeCompare(b.time ?? '');
}

/** One-off meetings whose title, date, attendees or items contain every word of `query`, ignoring case. */
export function filterOneOffs<M extends StandaloneMeeting>(meetings: M[], items: StandaloneItems, query: string): M[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return meetings;
  const extra = new Map<string, string[]>();
  const add = (id: string, text: string) => extra.set(id, [...(extra.get(id) ?? []), text]);
  for (const t of items.tasks) add(t.meeting_id, t.title);
  for (const n of items.notes) add(n.meeting_id, n.title);
  for (const a of items.attendees) add(a.meeting_id, a.name);
  return meetings.filter((m) => {
    const hay = [m.title ?? '', formatShortDate(m.meeting_date), ...(extra.get(m.id) ?? [])].join('\n').toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

interface UpcomingMeeting {
  id: string;
  series_id: string | null;
  title: string | null;
  meeting_date: string;
  meeting_time: string | null;
  duration_minutes: number | null;
  series_name: string | null;
}

interface UpcomingData {
  dates: string[];
  meetings: UpcomingMeeting[];
}

interface Suggestion {
  seriesId: string;
  seriesName: string;
  date: string;
  time: string | null;
  duration: number | null;
}

export function MeetingsPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [series, setSeries] = useState<MeetingSeries[]>([]);
  const [standaloneMeetings, setStandaloneMeetings] = useState<StandaloneMeeting[]>([]);
  const [standaloneItems, setStandaloneItems] = useState<StandaloneItems>(NO_ITEMS);
  const [oneOffFilter, setOneOffFilter] = useState('');
  const [seriesFilter, setSeriesFilter] = useState('');
  const [showAllSeries, setShowAllSeries] = useState(false);
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [upcomingData, setUpcomingData] = useState<UpcomingData | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [creatingSuggestion, setCreatingSuggestion] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStandaloneModal, setShowStandaloneModal] = useState(false);
  const [editingSeries, setEditingSeries] = useState<MeetingSeries | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  async function loadData() {
    try {
      const [seriesData, standaloneData, upcoming, oneOffItems] = await Promise.all([
        apiGet<MeetingSeries[]>('/meeting-series'),
        apiGet<StandaloneMeeting[]>('/meeting-instances/standalone'),
        apiGet<UpcomingData>('/meeting-instances/upcoming'),
        // The list still works without its items.
        apiGet<StandaloneItems>('/meeting-instances/standalone/items').catch(() => NO_ITEMS),
      ]);
      setSeries(seriesData);
      setStandaloneMeetings(standaloneData);
      setStandaloneItems({
        tasks: Array.isArray(oneOffItems?.tasks) ? oneOffItems.tasks : [],
        notes: Array.isArray(oneOffItems?.notes) ? oneOffItems.notes : [],
        attendees: Array.isArray(oneOffItems?.attendees) ? oneOffItems.attendees : [],
      });
      setUpcomingData(upcoming);

      // Load suggestions for series with cadence (not adhoc, not empty)
      const eligibleSeries = seriesData.filter(s => s.cadence && s.cadence !== 'adhoc' && !s.end_date);
      if (eligibleSeries.length > 0) {
        const nextDates = await Promise.all(
          eligibleSeries.map(s =>
            apiGet<{ date: string; time: string | null; duration: number | null }>(`/meeting-series/${s.id}/next-date`)
              .then(r => ({ seriesId: s.id, seriesName: s.name, ...r }))
              .catch(() => null)
          )
        );
        // Filter: only suggest if no instance exists on that date
        const existingDates = new Set(upcoming.meetings.map(m => `${m.series_id}:${m.meeting_date}`));
        const valid = nextDates.filter((s): s is Suggestion =>
          s !== null && typeof s.date === 'string' && !existingDates.has(`${s.seriesId}:${s.date}`)
        );
        setSuggestions(valid.sort(bySoonest));
      } else {
        setSuggestions([]);
      }
    } catch (err) {
      console.error('Failed to load meetings:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateSuggestion(suggestion: Suggestion) {
    setCreatingSuggestion(suggestion.seriesId);
    try {
      const instance = await apiPost<{ id: string }>(`/meeting-series/${suggestion.seriesId}/instances`, {
        meeting_date: suggestion.date,
        meeting_time: suggestion.time,
      });
      toast.success('Meeting created');
      navigate(`/meetings/${suggestion.seriesId}/${instance.id}`);
    } catch (err) {
      console.error('Failed to create meeting:', err);
      toast.error('Failed to create meeting');
    } finally {
      setCreatingSuggestion(null);
    }
  }

  useEffect(() => { loadData(); }, []);

  async function handleCreate(name: string, description: string, cadence: string, templateId?: string, schedulingConfig?: Record<string, unknown>) {
    try {
      let template_content: string | null = null;
      if (templateId && templateId !== 'blank') {
        const tmpl = await apiGet<MeetingTemplate>(`/meeting-templates/${templateId}`);
        template_content = JSON.stringify(tmpl.content);
      }
      const scheduling_config = schedulingConfig && Object.keys(schedulingConfig).length > 0 ? schedulingConfig : undefined;
      await apiPost('/meeting-series', { name, description: description || null, cadence: cadence || null, template_content, scheduling_config });
      setShowCreateModal(false);
      toast.success('Meeting series created');
      loadData();
    } catch (err) {
      console.error('Failed to create meeting series:', err);
      toast.error('Failed to create meeting series');
    }
  }

  async function handleUpdate(id: string, name: string, description: string, cadence: string) {
    try {
      await apiPatch(`/meeting-series/${id}`, { name, description: description || null, cadence: cadence || null });
      setEditingSeries(null);
      toast.success('Meeting series updated');
      loadData();
    } catch (err) {
      console.error('Failed to update meeting series:', err);
      toast.error('Failed to update meeting series');
    }
  }

  async function handleCreateStandalone(title: string, meetingDate: string, meetingTime: string, duration?: number) {
    try {
      await apiPost('/meeting-instances', { title, meeting_date: meetingDate, meeting_time: meetingTime || null, duration_minutes: duration || null });
      setShowStandaloneModal(false);
      toast.success('Meeting created');
      loadData();
    } catch (err) {
      console.error('Failed to create meeting:', err);
      toast.error('Failed to create meeting');
    }
  }

  async function handleDeleteStandalone(id: string) {
    const excerptNote = await excerptDeleteWarning('meeting_instance', id);
    if (!await confirm({ title: 'Delete this meeting?', message: excerptNote || undefined, variant: 'danger', confirmLabel: 'Delete' })) return;
    try {
      await apiDelete(`/meeting-instances/${id}`);
      toast.success('Meeting deleted');
      loadData();
    } catch (err) {
      console.error('Failed to delete meeting:', err);
      toast.error('Failed to delete meeting');
    }
  }

  async function handleDelete(id: string) {
    if (!await confirm({ title: 'Delete this series and all its meetings?', variant: 'danger', confirmLabel: 'Delete' })) return;
    try {
      await apiDelete(`/meeting-series/${id}`);
      toast.success('Meeting series deleted');
      loadData();
    } catch (err) {
      console.error('Failed to delete meeting series:', err);
      toast.error('Failed to delete meeting series');
    }
  }

  const filteredOneOffs = filterOneOffs(standaloneMeetings, standaloneItems, oneOffFilter);
  const filteredSeries = filterSeries(series, seriesFilter);
  const SERIES_FILTER_FROM = 7;
  // A filter shows every match; otherwise long lists stop at SHOWN_BEFORE_MORE.
  const seriesCapped = !showAllSeries && !seriesFilter.trim() && filteredSeries.length > SHOWN_BEFORE_MORE;
  const shownSeries = seriesCapped ? filteredSeries.slice(0, SHOWN_BEFORE_MORE) : filteredSeries;
  const suggestionsCapped = !showAllSuggestions && suggestions.length > SHOWN_BEFORE_MORE;
  const shownSuggestions = suggestionsCapped ? suggestions.slice(0, SHOWN_BEFORE_MORE) : suggestions;

  if (loading) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Meetings</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowStandaloneModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-bg-card border border-border text-text-secondary text-sm font-medium hover:bg-bg-hover transition-colors"
          >
            <Plus size={16} />
            One-Off
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
          >
            <Plus size={16} />
            New Series
          </button>
        </div>
      </div>

      {/* Upcoming Meetings */}
      {upcomingData && (() => {
        const today = getLocalToday();
        const now = new Date();
        // Filter: hide today's meetings that are past their end time
        const filtered = upcomingData.meetings.filter(m => {
          if (m.meeting_date !== today || !m.meeting_time) return true;
          const [mh, mm] = m.meeting_time.split(':').map(Number);
          const duration = m.duration_minutes ?? 60;
          const meetingEnd = mh * 60 + mm + duration;
          const nowMinutes = now.getHours() * 60 + now.getMinutes();
          return nowMinutes < meetingEnd;
        });
        if (filtered.length === 0 && suggestions.length === 0) return null;
        // Group by date
        const grouped = new Map<string, UpcomingMeeting[]>();
        for (const m of filtered) {
          if (!grouped.has(m.meeting_date)) grouped.set(m.meeting_date, []);
          grouped.get(m.meeting_date)!.push(m);
        }
        return (
          <div className="mb-8">
            <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">Upcoming</h2>
            <div className="space-y-4">
              {upcomingData.dates.map(date => {
                const meetings = grouped.get(date);
                if (!meetings || meetings.length === 0) return null;
                return (
                  <div key={date}>
                    <h3 className="text-xs font-medium text-text-secondary mb-1.5">{formatDayLabel(date)}</h3>
                    <div className="space-y-1">
                      {meetings.map(m => (
                        <Link
                          key={m.id}
                          to={m.series_id ? `/meetings/${m.series_id}/${m.id}` : `/meetings/standalone/${m.id}`}
                          className="flex items-center gap-3 px-4 py-2.5 bg-bg-card border border-border rounded-lg hover:border-border-hover transition-colors group"
                        >
                          {m.meeting_time && (
                            <span className="text-xs font-medium text-accent flex-shrink-0">
                              {formatTime(m.meeting_time)}
                              {m.duration_minutes && <span className="text-text-muted font-normal"> · {formatDuration(m.duration_minutes)}</span>}
                            </span>
                          )}
                          {!m.meeting_time && <span className="w-16 flex-shrink-0" />}
                          <span className="text-sm text-text-primary group-hover:text-accent transition-colors truncate">
                            {m.title || m.series_name || 'Untitled'}
                          </span>
                          {m.series_name && m.title && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-bg-hover rounded-full text-text-muted flex-shrink-0">
                              {m.series_name}
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Series Suggestions */}
              {suggestions.length > 0 && (
                <div>
                  <h3 className="text-xs font-medium text-text-muted mb-1.5 flex items-center gap-1">
                    <Sparkles size={10} /> Suggested
                  </h3>
                  <div data-suggestions data-faded={suggestionsCapped || undefined} className="space-y-1" style={fadeStyle(suggestionsCapped)}>
                    {shownSuggestions.map(s => (
                      <div
                        key={s.seriesId}
                        className="flex items-center gap-3 px-4 py-2.5 border border-dashed border-border rounded-lg"
                      >
                        {s.time && (
                          <span className="text-xs font-medium text-text-muted flex-shrink-0">
                            {formatTime(s.time)}
                            {s.duration && <span className="font-normal"> · {formatDuration(s.duration)}</span>}
                          </span>
                        )}
                        {!s.time && <span className="w-16 flex-shrink-0" />}
                        <span className="text-sm text-text-muted truncate flex-1">
                          {s.seriesName}
                        </span>
                        <span className="text-xs text-text-muted flex-shrink-0">{formatDayLabel(s.date)}</span>
                        <button
                          onClick={() => handleCreateSuggestion(s)}
                          disabled={creatingSuggestion === s.seriesId}
                          className="text-xs px-2.5 py-1 bg-accent/10 text-accent rounded-md hover:bg-accent/20 transition-colors disabled:opacity-50 flex-shrink-0"
                        >
                          {creatingSuggestion === s.seriesId ? 'Creating...' : 'Create'}
                        </button>
                      </div>
                    ))}
                  </div>
                  {suggestions.length > SHOWN_BEFORE_MORE && (
                    <MoreToggle open={!suggestionsCapped} label={`Show ${suggestions.length - SHOWN_BEFORE_MORE} more`}
                      onToggle={() => setShowAllSuggestions((v) => !v)} />
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Meeting Series */}
      {series.length > 0 && (
        <div className="mb-8">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider">Meeting Series</h2>
            {series.length >= SERIES_FILTER_FROM && (
              <label className="relative w-full sm:w-64">
                <Search size={14} aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                  type="search"
                  value={seriesFilter}
                  onChange={(e) => setSeriesFilter(e.target.value)}
                  placeholder="Filter series"
                  aria-label="Filter meeting series"
                  maxLength={200}
                  className="w-full rounded-md border border-border bg-bg-input py-1.5 pl-8 pr-2 text-sm text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
                />
              </label>
            )}
          </div>
          {filteredSeries.length === 0 && (
            <p className="py-6 text-center text-sm text-text-muted">No meeting series match "{seriesFilter.trim()}".</p>
          )}
          {/* Compact cards, one to three across, so dozens of series stay scannable (#319). */}
          <div data-series-grid data-faded={seriesCapped || undefined} className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3" style={fadeStyle(seriesCapped)}>
            {shownSeries.map((s) => (
              <div
                key={s.id}
                className={`min-w-0 bg-bg-card border border-border rounded-lg px-3 py-2.5 hover:border-border-hover transition-colors group ${s.end_date ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start justify-between gap-1">
                  <Link to={`/meetings/${s.id}`} className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-text-primary group-hover:text-accent transition-colors truncate">
                      {s.name}
                    </h3>
                    {s.description && (
                      <p className="text-xs text-text-muted mt-0.5 truncate">{s.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1 text-xs text-text-muted">
                      {s.cadence && <span className="capitalize">{s.cadence}</span>}
                      <span>{s.instance_count} meeting{s.instance_count !== 1 ? 's' : ''}</span>
                      {s.end_date && (
                        <span className="px-1.5 py-0.5 rounded bg-warning/10 text-warning text-[10px] font-medium">
                          Ended
                        </span>
                      )}
                    </div>
                  </Link>
                  <div className="relative">
                    <button
                      onClick={(e) => { e.preventDefault(); setMenuOpenId(menuOpenId === s.id ? null : s.id); }}
                      className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
                    >
                      <MoreVertical size={16} />
                    </button>
                    {menuOpenId === s.id && (
                      <div className="absolute right-0 top-8 z-10 w-36 bg-bg-surface border border-border rounded-md shadow-lg py-1">
                        <button
                          onClick={() => { setEditingSeries(s); setMenuOpenId(null); }}
                          className="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-hover transition-colors"
                        >
                          <Pencil size={14} /> Edit
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              await apiPatch(`/meeting-series/${s.id}/archive`, {});
                              toast.success('Series archived');
                              setSeries(prev => prev.filter(x => x.id !== s.id));
                            } catch { toast.error('Failed to archive series'); }
                            setMenuOpenId(null);
                          }}
                          className="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-hover transition-colors"
                        >
                          <Archive size={14} /> Archive
                        </button>
                        <button
                          onClick={() => { handleDelete(s.id); setMenuOpenId(null); }}
                          className="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-danger hover:bg-bg-hover transition-colors"
                        >
                          <Trash2 size={14} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {!seriesFilter.trim() && filteredSeries.length > SHOWN_BEFORE_MORE && (
            <MoreToggle open={!seriesCapped} label={`Show all ${filteredSeries.length}`} onToggle={() => setShowAllSeries((v) => !v)} />
          )}
        </div>
      )}

      {/* One-Off Meetings */}
      {standaloneMeetings.length > 0 && (
        <div className="mb-8">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider">One-Off Meetings</h2>
            <label className="relative w-full sm:w-64">
              <Search size={14} aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
              <input
                type="search"
                value={oneOffFilter}
                onChange={(e) => setOneOffFilter(e.target.value)}
                placeholder="Filter by title, person or item"
                aria-label="Filter one-off meetings"
                maxLength={200}
                className="w-full rounded-md border border-border bg-bg-input py-1.5 pl-8 pr-2 text-sm text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
              />
            </label>
          </div>
          {filteredOneOffs.length === 0 && (
            <p className="py-6 text-center text-sm text-text-muted">No one-off meetings match "{oneOffFilter.trim()}".</p>
          )}
          {filteredOneOffs.length > 0 && <SeriesMeetingList
            key={oneOffFilter.trim() ? `filter:${oneOffFilter.trim().toLowerCase()}` : 'all'}
            expandAll={!!oneOffFilter.trim()}
            meetingHref={(id) => `/meetings/standalone/${id}`}
            meetings={filteredOneOffs}
            today={getLocalToday()}
            itemsByMeeting={buildMeetingItems({
              meetingHref: (id) => `/meetings/standalone/${id}`,
              tasks: standaloneItems.tasks,
              notes: standaloneItems.notes,
              followUps: standaloneItems.tasks.filter((t) => t.follow_up).map((t) => ({ ...t, source_meeting_id: t.meeting_id })),
              excerpts: [],
            })}
            withNotes={new Set(standaloneMeetings.filter((m) => hasWrittenNotes(m.note_content, null)).map((m) => m.id))}
            formatWhen={(m) => {
              const when = [m.meeting_time && formatTime(m.meeting_time), m.duration_minutes && formatDuration(m.duration_minutes)]
                .filter(Boolean).join(' - ') || null;
              return { heading: m.title || 'Untitled Meeting', sub: [formatShortDate(m.meeting_date), when].filter(Boolean).join(' - '), when };
            }}
            actions={(m) => (
              <button
                type="button"
                onClick={() => handleDeleteStandalone(m.id)}
                aria-label={`Delete ${m.title || 'meeting'}`}
                className="p-1 text-text-muted hover:text-danger transition-colors"
              >
                <Trash2 size={14} />
              </button>
            )}
          />}
        </div>
      )}

      {/* Empty state */}
      {series.length === 0 && standaloneMeetings.length === 0 && (
        <div className="text-center py-16 text-text-muted">
          <Calendar size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">No meetings yet</p>
          <p className="text-sm">Create a meeting series or a one-off meeting to get started.</p>
        </div>
      )}

      {(showCreateModal || editingSeries) && (
        <SeriesModal
          series={editingSeries}
          onSave={(name, desc, cadence, templateId, schedulingConfig) =>
            editingSeries
              ? handleUpdate(editingSeries.id, name, desc, cadence)
              : handleCreate(name, desc, cadence, templateId, schedulingConfig)
          }
          onClose={() => { setShowCreateModal(false); setEditingSeries(null); }}
        />
      )}

      {showStandaloneModal && (
        <StandaloneModal
          onSave={handleCreateStandalone}
          onClose={() => setShowStandaloneModal(false)}
        />
      )}
    </div>
    </div>
  );
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function SeriesModal({
  series,
  onSave,
  onClose,
}: {
  series: MeetingSeries | null;
  onSave: (name: string, description: string, cadence: string, templateId?: string, schedulingConfig?: Record<string, unknown>) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(series?.name ?? '');
  const [description, setDescription] = useState(series?.description ?? '');
  const [cadence, setCadence] = useState(series?.cadence ?? '');
  const [templateId, setTemplateId] = useState('blank');
  const [templates, setTemplates] = useState<MeetingTemplate[]>([]);
  const [schedTime, setSchedTime] = useState('');
  const [schedDuration, setSchedDuration] = useState('');
  const [schedDaysOfWeek, setSchedDaysOfWeek] = useState<number[]>([]);
  const [schedMonthlyMode, setSchedMonthlyMode] = useState('');
  const [schedDayOfMonth, setSchedDayOfMonth] = useState(1);
  const [schedOrdinal, setSchedOrdinal] = useState(1);
  const [schedOrdinalDay, setSchedOrdinalDay] = useState(1);

  useEffect(() => {
    if (!series) {
      apiGet<MeetingTemplate[]>('/meeting-templates')
        .then(setTemplates)
        .catch(console.error);
    }
  }, [series]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-4">
          {series ? 'Edit Meeting Series' : 'New Meeting Series'}
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Weekly Ops Sync"
              autoFocus
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={2}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm resize-none"
            />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Cadence</label>
            <select
              value={cadence}
              onChange={(e) => setCadence(e.target.value)}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm"
            >
              <option value="">None</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Biweekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="adhoc">Ad hoc</option>
            </select>
          </div>
          {/* Scheduling options — shown when a cadence is selected */}
          {!series && cadence && cadence !== 'adhoc' && (
            <div className="space-y-3 p-3 bg-bg-card rounded-lg border border-border">
              <p className="text-xs text-text-muted">Schedule details</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-text-secondary mb-1">Default Time</label>
                  <TimeSelect
                    value={schedTime}
                    onChange={setSchedTime}
                    className="w-full px-2.5 py-1.5 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-secondary mb-1">Duration</label>
                  <select
                    value={schedDuration}
                    onChange={(e) => setSchedDuration(e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm"
                  >
                    <option value="">—</option>
                    <option value="15">15 min</option>
                    <option value="30">30 min</option>
                    <option value="45">45 min</option>
                    <option value="60">1 hour</option>
                    <option value="90">1.5 hours</option>
                    <option value="120">2 hours</option>
                  </select>
                </div>
              </div>
              {(cadence === 'weekly' || cadence === 'biweekly') && (
                <div>
                  <label className="block text-xs text-text-secondary mb-1">Days</label>
                  <div className="flex flex-wrap gap-1">
                    {DAY_SHORT.map((label, i) => {
                      const selected = schedDaysOfWeek.includes(i);
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setSchedDaysOfWeek(prev =>
                            selected ? prev.filter(d => d !== i) : [...prev, i].sort()
                          )}
                          className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                            selected
                              ? 'bg-accent/20 border-accent text-accent font-medium'
                              : 'bg-bg-input border-border text-text-muted hover:border-border-hover'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {(cadence === 'monthly' || cadence === 'quarterly') && (
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs text-text-secondary mb-1">Repeat on</label>
                    <select
                      value={schedMonthlyMode}
                      onChange={(e) => setSchedMonthlyMode(e.target.value)}
                      className="w-full px-2.5 py-1.5 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm"
                    >
                      <option value="">Auto</option>
                      <option value="date">Day of month</option>
                      <option value="ordinal">Day of week</option>
                    </select>
                  </div>
                  {schedMonthlyMode === 'date' && (
                    <select
                      value={schedDayOfMonth}
                      onChange={(e) => setSchedDayOfMonth(Number(e.target.value))}
                      className="w-full px-2.5 py-1.5 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm"
                    >
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  )}
                  {schedMonthlyMode === 'ordinal' && (
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={schedOrdinal}
                        onChange={(e) => setSchedOrdinal(Number(e.target.value))}
                        className="px-2.5 py-1.5 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm"
                      >
                        <option value={1}>1st</option>
                        <option value={2}>2nd</option>
                        <option value={3}>3rd</option>
                        <option value={4}>4th</option>
                        <option value={-1}>Last</option>
                      </select>
                      <select
                        value={schedOrdinalDay}
                        onChange={(e) => setSchedOrdinalDay(Number(e.target.value))}
                        className="px-2.5 py-1.5 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm"
                      >
                        {DAY_NAMES.map((name, i) => (
                          <option key={i} value={i}>{name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {!series && templates.length > 0 && (
            <div>
              <label className="block text-sm text-text-secondary mb-1">Template</label>
              <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto">
                {templates.map(tmpl => (
                  <button
                    key={tmpl.id}
                    type="button"
                    onClick={() => setTemplateId(tmpl.id)}
                    className={`flex items-start gap-2 px-2.5 py-2 rounded-md border text-left transition-colors ${
                      templateId === tmpl.id
                        ? 'border-accent bg-accent/10 text-accent'
                        : 'border-border bg-bg-input text-text-secondary hover:border-border-hover hover:bg-bg-hover'
                    }`}
                  >
                    <FileText size={14} className="flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-xs font-medium block">{tmpl.name}</span>
                      <span className="text-[10px] text-text-muted block">{tmpl.description}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (!name.trim()) return;
              const config: Record<string, unknown> = {};
              if (schedTime) config.default_time = schedTime;
              if (schedDuration) config.default_duration = Number(schedDuration);
              if (schedDaysOfWeek.length > 0) config.days_of_week = schedDaysOfWeek;
              if (schedMonthlyMode === 'date') config.day_of_month = schedDayOfMonth;
              if (schedMonthlyMode === 'ordinal') {
                config.monthly_ordinal = schedOrdinal;
                config.monthly_weekday = schedOrdinalDay;
              }
              onSave(name.trim(), description, cadence, templateId, config);
            }}
            disabled={!name.trim()}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover transition-colors disabled:opacity-50"
          >
            {series ? 'Save Changes' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StandaloneModal({
  onSave,
  onClose,
}: {
  onSave: (title: string, meetingDate: string, meetingTime: string, duration?: number) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().split('T')[0]);
  const [meetingTime, setMeetingTime] = useState('');
  const [duration, setDuration] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-4">New One-Off Meeting</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Coffee Chat with John"
              autoFocus
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Date *</label>
              <input
                type="date"
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
                className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Time</label>
              <TimeSelect
                value={meetingTime}
                onChange={setMeetingTime}
                className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Duration</label>
              <select
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm"
              >
                <option value="">—</option>
                <option value="15">15 min</option>
                <option value="30">30 min</option>
                <option value="45">45 min</option>
                <option value="60">1 hour</option>
                <option value="90">1.5 hours</option>
                <option value="120">2 hours</option>
              </select>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => title.trim() && meetingDate && onSave(title.trim(), meetingDate, meetingTime, duration ? Number(duration) : undefined)}
            disabled={!title.trim() || !meetingDate}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover transition-colors disabled:opacity-50"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

function formatShortDate(d: string): string {
  return new Date(d + (d.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTime(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatDayLabel(dateStr: string): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + 'T00:00:00');
  const diff = Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}
